import { Component, OnInit, Input, ElementRef, ViewChild, HostListener, ViewChildren, QueryList } from '@angular/core';
import { IParameter } from 'app/_models/IParameter';
import { AppComponent } from 'app/app.component';
import { ISelectedLine } from 'app/_models/ISelectedLine';
import { CommunicationService } from 'app/_services/communication.service';
import { IAction } from 'app/_models/IAction';
import { RADController } from 'app/_controllers/radController';
import { IHeader } from 'app/_models/IHeader';
import { IGanttTask } from 'app/_models/IGanttTask';
import { ISourceViewHeader } from 'app/_models/ISourceViewHeader';
import { ISettings } from 'app/_models/ISettings';
import SignaturePad from 'signature_pad';
import { parse } from 'node-html-parser';
import {
  IconDefinition, faPen, faXmark, faCircleInfo, faArrowUpFromBracket, faChevronDown,
  faTrash, faFloppyDisk, faTriangleExclamation, faCheckDouble, faAngleDoubleDown, faListCheck
} from '@fortawesome/free-solid-svg-icons';
import { OverlayPanel } from 'primeng/overlaypanel';
import { IParamLayout } from 'app/_models/IParamLayout';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Component({
  selector: 'form-action-component',
  templateUrl: './form-action.component.html',
  styleUrls: ['./form-action.component.css']
})
export class FormActionComponent implements OnInit {
  //#region Fields
  @Input() settings: ISettings;
  @Input() headers: IHeader[] | ISourceViewHeader[] = [];
  @Input() selectedItems: ISelectedLine[] | IGanttTask[] = [];
  @Input() action: IAction;
  @Input() nodeId: string;
  @Input() userId: string;
  @Input() username: string;
  @Input() mobileView: boolean;
  parameters: IParameter[] = [];
  showConfirmation: boolean = false;
  result: string;
  j: number;
  invalidValues: boolean = false;
  validTestedSQL: boolean = false;
  showDataPreview: boolean = false;
  tableColumns: any;
  tableData: any;
  signaturePad!: SignaturePad;
  @ViewChild('canvas') canvasEl!: ElementRef;
  signatureImg: string = undefined;
  actionHasSignature: boolean = false;
  updateFeedbackMessage: string = '';
  showDateFields: boolean = false;
  paramRows: IParamLayout[][];
  //#endregion
  //#region Icons
  faPen: IconDefinition = faPen;
  faXmark: IconDefinition = faXmark;
  faCircleInfo: IconDefinition = faCircleInfo;
  faArrowUpFromBracket: IconDefinition = faArrowUpFromBracket;
  faTrash: IconDefinition = faTrash;
  faFloppyDisk: IconDefinition = faFloppyDisk;
  faTriangleExclamation: IconDefinition = faTriangleExclamation;
  faCheckDouble: IconDefinition = faCheckDouble;
  faAngleDoubleDown: IconDefinition = faAngleDoubleDown;
  faChevronDown: IconDefinition = faChevronDown;
  faListCheck: IconDefinition = faListCheck;
  //#endregion  

  constructor(
    public app: AppComponent,
    private communicationService: CommunicationService,
    private radController: RADController,
    private sanitizer: DomSanitizer
  ) { }

  ngOnInit(): void {
    this.app.showLoader();
    this.parameters = this.action.parameters;    
    this.loadSourceViewHeaders();
  }

  ngAfterViewInit(): void {
    if (this.canvasEl) this.signaturePad = new SignaturePad(this.canvasEl.nativeElement);
  }

  async loadSourceViewHeaders(): Promise<void> {
    this.parameters = await this.radController.loadSourceViewHeaders(this.parameters, this.nodeId, this.userId);
    this.initialise();
  }

  initialise(): void {
    let childParams = this.parameters.filter(param => param.filterParameter != null);
    this.parameters.forEach(param => {
      param.highlightRequired = false;
      param.dateValues = { userDate: '', serverDate: '' };
      param.isParentLookup = childParams.some(childParam => param.caption === childParam.filterParameter);
      param.preselectedValue = '';
      param.errorMessage = '';
      param.opened = false;
      param.multiValueArray = [];
      param.selectedComboId = '-1';

      if (param.type == 'combolookup' && param.data.includes('@USERID'))
        param.data = param.data.replace(/@USERID/g, this.userId);

      if (param.type == 'combo' || param.type == 'combobox') {
        param.lookupValues = param.data.split(',');
      }

      if (param.type.includes('check')) param.preselectedValue = null;

      let headersToCheck = param.sourceViewHeaders.length > 0 ? param.sourceViewHeaders : this.headers;
      let header = headersToCheck.find(header => header.caption == param.caption);
      if (header) param.dataType = header.dataType;
      else param.dataType = '';
    });

    if (this.selectedItems.length == 1 && this.action.hasEditProc) this.preselectParameters();
    else {
      this.showDateFields = true;
      this.j = 0;
      this.loadLookups(false, this.parameters);
      this.paramRows = this.buildLayout(this.parameters);
    }
  }

  preselectParameters(): void {    
    let selectedLine = this.isSelectedLine(this.selectedItems[0]) ? this.selectedItems[0].lineData : this.selectedItems[0];   
    for (let key in selectedLine) {      
      let parameter = this.parameters.find(parameter => parameter.caption.toLowerCase() == key.toLowerCase() || parameter.sourceColumn?.toLowerCase() == key.toLowerCase());
      if (parameter) {
        parameter.preselectedValue = '';
        parameter.value = '';
        parameter.dateValues = { userDate: '', serverDate: '' };

        let lineValue = selectedLine[key];
        if (lineValue != null) {
          if ((parameter.dataType == 'DateTime' && parameter.type.includes('date')) || (parameter.dataType == '' && (parameter.type == 'datetimebox' || parameter.type == 'datebox'))) {
            parameter.dateValues.userDate = lineValue.toString();

            let dateValues = lineValue.split('/');
            if (this.settings.dateFormatSetting == 'dd/MM/yyyy')
              parameter.dateValues.serverDate = `${dateValues[1]}/${dateValues[0]}/${dateValues[2]}`;

            if (this.settings.dateFormatSetting == 'yyyy/MM/dd')
              parameter.dateValues.serverDate = `${dateValues[1]}/${dateValues[2]}/${dateValues[0]}`;

            this.showDateFields = true;
          } else if (parameter.type.includes('check')) {
            if (lineValue == undefined || lineValue.toString() == '' || lineValue == null) {
              parameter.preselectedValue = null;
              parameter.value = null;
            } else {
              parameter.preselectedValue = lineValue.toString();
              parameter.value = lineValue.toString();
            }
          } else if (parameter.type == 'multiselect' && lineValue.toString() != '') {
            parameter.value = lineValue.toString() + ',';
            parameter.multiValueArray = lineValue.toString().split(',');
          } else {             
            let parsedValue = '';
            if (lineValue && typeof lineValue === 'object' && 'changingThisBreaksApplicationSecurity' in lineValue) {
              parsedValue = lineValue.changingThisBreaksApplicationSecurity.includes('</div>') ? parse(lineValue.toString()).querySelector('div')?.text || '' : lineValue.changingThisBreaksApplicationSecurity;
            } else {
              parsedValue = !lineValue.toString().includes('</div>') ? lineValue.toString() : parse(lineValue.toString()).querySelector('div')?.text || '';
            }
            
            parameter.preselectedValue = parsedValue;
            parameter.value = parsedValue;
          }

          if (parameter.isParentLookup) {
            let childParams = this.parameters.filter(param => param.filterParameter != null && param.filterParameter == parameter.caption);
            childParams.forEach(childParam => { childParam.filterValue = parameter.preselectedValue; })
          }
        }
      }
    }
    
    this.j = 0;
    this.loadLookups(false, this.parameters);
    this.paramRows = this.buildLayout(this.parameters);
  }  

  //#region Layout
  getOverlayPanelWidth(item: IParamLayout): string {
    if (`col-${item.width}` === 'col-12') return 'overlay-a';
    if (`col-${item.width}` === 'col-6') return 'overlay-b';
    return 'overlay-a';
  }

  getColClasses(item: IParamLayout): string {
    if (window.innerWidth <= 500) {
      return 'col-12';
    }

    let classes = [`col-${item.width}`];
    if (item.offset > 0) classes.push(`offset-${item.offset}`);
    return classes.join(' ');
  }

  computeRowLayout(row: IParamLayout[]): void {
    let totalCols = Math.max(...row.map(x => x.col + x.span - 1));
    let prevEnd = 0;
    row.forEach(item => {
      let gridOffset = item.col - prevEnd - 1;
      let offset = gridOffset > 0 ? gridOffset : 0;
      item.width = (item.span / totalCols) * 12;
      item.offset = (offset / totalCols) * 12;
      prevEnd = item.col + item.span - 1;
    });
  }

  parsePosition(param: IParameter): IParamLayout {
    if (param.position) {
      let parts = param.position.split(',').map(v => Number(v.trim()));
      let row = parts[0];
      let col = parts.length > 1 ? parts[1] : 1;
      let span = parts.length > 2 ? parts[2] : 1;
      return { row, col,  span, param };
    }

    return { row: 1, col: 1, span: 1, param };
  }

  buildLayout(params: IParameter[]): IParamLayout[][] {
    let rows: { [key: number]: IParamLayout[] } = {};

    params.forEach(p => {
      let item = this.parsePosition(p);
      if (!rows[item.row]) rows[item.row] = [];
      rows[item.row].push(item);
    });

    let result = Object.keys(rows).sort((a, b) => +a - +b).map(r => rows[+r].sort((a, b) => a.col - b.col));
    result.forEach(row => this.computeRowLayout(row));
    return result;
  }
  //#endregion

  //#region Combos
  updateMultiSelectCombos(event: any, param: IParameter, selectedLookupValue: any): void {
    if (event.checked) {
      param.multiValueArray.push(selectedLookupValue);
      param.value += selectedLookupValue + ',';
    } else {
      param.value = param.value.replace(`${selectedLookupValue},`, '');
      let index = param.multiValueArray.indexOf(selectedLookupValue);
      if (index !== -1) param.multiValueArray.splice(index, 1);
    }
  }

  unselectOption(value: string, param: IParameter): void {
    param.value = param.value.replace(`${value},`, '');
    let index = param.multiValueArray.indexOf(value);
    if (index !== -1) param.multiValueArray.splice(index, 1);
  }

  openDropdown(event: Event, param: IParameter, op: OverlayPanel): void {
    param.opened = true;
    this.openOptions(param);
    op.toggle(event);
  }

  containsString(arr: any[], searchStr: string): boolean {
    return arr.some(item => {
      if (typeof item === 'string') {
        return item.includes(searchStr);
      } else if ('value' in item) {
        return item.value.includes(searchStr);
      }
      return false;
    });
  }

  handleBlur(param: IParameter, op: OverlayPanel): void {
    param.opened = false;
    op.hide();

    if (param.value != '') {
      let res = this.containsString(param.lookupValues, param.value);
      if (!res) {
        param.value = '';
        param.selectedComboId = '-1';
        let childParams = this.parameters.filter(p => p.filterParameter != null && p.filterParameter == param.caption);
        childParams.forEach(childParam => {
          childParam.value = "";
          childParam.filterValue = "";
          childParam.lookupValues = [];
          childParam.filteredLookupValues = [];
        })
      }
    } else {
      if (param.wasTabSelecting) {
        if (param.filteredLookupValues.length > 0) {
          let lookupValue = param.filteredLookupValues.find(l => l.selected);
          if (lookupValue) {
            param.value = param.type == 'combolookup' ? lookupValue.lookUpValue.value : lookupValue.lookUpValue;
            param.selectedComboId = lookupValue.lookUpValue.id == '-1' ? lookupValue.lookUpValue.value : lookupValue.lookUpValue.id;

            let childParams = this.parameters.filter(p => p.filterParameter != null && p.filterParameter == param.caption);
            childParams.forEach(childParam => {
              childParam.value = "";
              childParam.filterValue = param.value;
            })

            this.j = 0;
            this.loadLookups(false, childParams);
          }

        }
      }
    }

    if (param.wasTabSelecting) {
      param.wasTabSelecting = false;
      return;
    }

    if (param.wasExplicitlySelected) {
      param.wasExplicitlySelected = false;
      return;
    }
  }

  loadLookups(fromTextArea: boolean, params: IParameter[]): void {
    if (this.j == params.length) {       
      this.app.hideLoader();      
      return;
    }

    let param = params[this.j];
    if (param.type == 'combolookup' || param.type == 'multiselect') {
      param.lookupValues = [];
      let id = this.selectedItems.length > 0 ? this.selectedItems[0].id : '';
      this.radController.getLookupData(param.data, param.filterValue, id, this.userId).then(result => {
        let parentTextField = this.parameters.find(p => p.isParentLookup && p.type == 'textarea');
        if (fromTextArea && result.length > 0 && result[0].id.includes('ERROR')) {
          if (parentTextField) {
            parentTextField.errorMessage = result[0].value;
            let childParams = this.parameters.filter(p => p.filterParameter != null && p.filterParameter == parentTextField.caption);
            childParams.forEach(childParam => {
              childParam.value = "";
              childParam.filterValue = "";
              childParam.lookupValues = [];
              childParam.filteredLookupValues = [];
            })
          }
        }
        else {
          param.lookupValues = result;
          param.filteredLookupValues = [];
          for (let index = 0; index < param.lookupValues.length; index++) {
            param.filteredLookupValues.push({ lookUpValue: param.lookupValues[index], selected: index == 0 ? true : false });
          }
        }
      });      
    }
   
    this.j++;
    this.loadLookups(fromTextArea, params);    
  }

  openOptions(param: IParameter): void {
    param.filteredLookupValues = [];
    for (let index = 0; index < param.lookupValues.length; index++) {
      let element = param.lookupValues[index];
      let selected = false;

      if (!param.value || param.value === '') {
        selected = index === 0;
      } else {
        if (param.type === 'combolookup') {
          selected = param.value === element.value;
        } else {
          selected = param.value === element;
        }
      }

      param.filteredLookupValues.push({ lookUpValue: element, selected });
    }

    param.opened = true;
  }

  updateCombos(updatedFromTextArea: boolean, parameter: IParameter, selectedLookupValue: any): void {
    if (!updatedFromTextArea) {
      if (parameter.type == 'combolookup') {
        parameter.value = selectedLookupValue.lookUpValue.value;
        parameter.preselectedValue = selectedLookupValue.lookUpValue.value;
        parameter.selectedComboId = selectedLookupValue.lookUpValue.id == '-1' ? selectedLookupValue.lookUpValue.value : selectedLookupValue.lookUpValue.id;
        let currentSelectedValue = parameter.filteredLookupValues.find(v => v.selected);
        if (currentSelectedValue) {
          currentSelectedValue.selected = false;
        }
        selectedLookupValue.selected = true;
      } else {
        parameter.value = selectedLookupValue.lookUpValue;
        parameter.preselectedValue = selectedLookupValue.lookUpValue;
        let currentSelectedValue = parameter.filteredLookupValues.find(v => v.selected);
        if (currentSelectedValue) {
          currentSelectedValue.selected = false;
        }
        selectedLookupValue.selected = true;
      }
    }

    parameter.opened = false;
    if (!parameter.isParentLookup) return;
    parameter.errorMessage = '';
    let childParams = this.parameters.filter(param => param.filterParameter != null && param.filterParameter == parameter.caption);
    childParams.forEach(childParam => {
      childParam.filterValue = parameter.value;
      childParam.value = "";
      childParam.lookupValues = [];
      childParam.filteredLookupValues = [];
    });
    this.j = 0;
    this.loadLookups(updatedFromTextArea, childParams);
  }

  change(event: KeyboardEvent, param: IParameter): void {
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown' || event.key === 'Enter' || event.key === 'Tab') return;
    if (param.value.length > 0) {
      if (param.type == 'combolookup') {
        let a = param.lookupValues.filter(item => item.value.toLowerCase().includes(param.value.toLowerCase()));
        param.filteredLookupValues = [];
        for (let index = 0; index < a.length; index++) {
          param.filteredLookupValues.push({ lookUpValue: a[index], selected: false });
        }
      } else {
        let a = param.lookupValues.filter(item => item.toLowerCase().includes(param.value.toLowerCase()));
        param.filteredLookupValues = [];
        for (let index = 0; index < a.length; index++) {
          param.filteredLookupValues.push({ lookUpValue: a[index], selected: false });
        }
      }

      if (param.filteredLookupValues.length > 0) param.filteredLookupValues[0].selected = true;
    }
    else {
      param.filteredLookupValues = [];
      for (let index = 0; index < param.lookupValues.length; index++) {
        param.filteredLookupValues.push({ lookUpValue: param.lookupValues[index], selected: false });
      }
    }
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Tab') {
      let param = this.parameters.find(p => p.opened);
      if (param) {
        if (!param.opened) return;
        param.wasTabSelecting = true;
      }
    }
  }

  @HostListener('window:keyup', ['$event'])
  handleKeyUp(event: KeyboardEvent): void {
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown' || event.key === 'Enter' || event.key === 'Tab') {
      let param = this.parameters.find(p => p.opened);
      if (param && !param.opened) return;

      if (!param || !param.filteredLookupValues) return;
      let selectedIndex = param.filteredLookupValues.findIndex(v => v.selected);
      if (selectedIndex === -1) return;

      if (event.key === 'ArrowUp' && selectedIndex > 0) {
        param.filteredLookupValues[selectedIndex].selected = false;
        param.filteredLookupValues[selectedIndex - 1].selected = true;
        param.value = '';
        param.selectedComboId = '-1';
        this.scrollToOption(selectedIndex - 1);
      }

      if (event.key === 'ArrowDown' && selectedIndex < param.filteredLookupValues.length - 1) {
        param.filteredLookupValues[selectedIndex].selected = false;
        param.filteredLookupValues[selectedIndex + 1].selected = true;
        param.value = '';
        param.selectedComboId = '-1';
        this.scrollToOption(selectedIndex + 1);
      }

      if (event.key === 'Enter') {
        param.wasExplicitlySelected = true;
        this.updateCombos(false, param, param.filteredLookupValues[selectedIndex]);
      }

      if (event.key === 'Tab') {
        if (selectedIndex >= 0) {
          param.wasExplicitlySelected = true;
          this.updateCombos(false, param, param.filteredLookupValues[selectedIndex]);
        }
      }
    }
  }

  @ViewChildren('optionEl', { read: ElementRef }) optionEls!: QueryList<ElementRef>;
  scrollToOption(index: number) {
    let element = this.optionEls.toArray()[index]?.nativeElement;
    if (element) element.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }
  //#endregion

  updateCheckboxToggles(parameter: IParameter, event: any): void {
    parameter.value = event.checked.toString();
    parameter.preselectedValue = event.checked.toString();
  }

  updateNumericInputs(parameter: IParameter, event: any): void {
    if (parameter.dataType == 'Int32') {
      let value = event.srcElement.value.toString();
      if (value.includes(',') || value.includes('.')) {
        this.app.alertError(`${this.app.translations.ALERT_Error_InvalidDecimal} ${parameter.caption}`);
        this.invalidValues = true;
        return;
      }
    }

    this.invalidValues = false;
    parameter.value = event.srcElement.value.toString();
    parameter.preselectedValue = event.srcElement.value.toString();
  }

  updateTextInputs(parameter: IParameter, event: any): void {
    parameter.value = event.srcElement.value.toString();
    parameter.preselectedValue = event.srcElement.value.toString();
  }

  updateDateInput(selectedDate: string, parameter: IParameter): void {
    parameter.dateValues.serverDate = selectedDate == 'null' ? null : selectedDate;
    parameter.value = selectedDate == 'null' ? null : selectedDate;
  }

  updateImage(parameter: IParameter, event: any): void {
    if (!event) {
      parameter.value = null;
      parameter.preselectedValue = null;
      if (this.selectedItems.length >= 2)
        this.app.alertInfo(this.app.translations.ALERT_Info_ImageRemoved);
      return;
    }

    let fileToUpload = event.target.files;
    let file = fileToUpload[0] as File;
    if (!file.name.includes('.png') && !file.name.includes('.jpg')) {
      this.app.alertError(this.app.translations.ALERT_Error_InvalidImage);
      return;
    }

    let reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      let base64String = reader.result as string;
      parameter.value = base64String.replace(`data:${file.type};base64,`, '');
      parameter.preselectedValue = parameter.value;
    };
  }

  updateSignature(parameter: IParameter): void {
    this.signatureImg = this.signaturePad.toDataURL();
    parameter.value = this.signatureImg.replace('data:image/png;base64,', '');
  }

  clearSignature(parameter: IParameter): void {
    this.signaturePad.clear();
    this.signatureImg = undefined;
    parameter.value = null;
  }

  uploadArtifact(parameter: IParameter, event: any): void {
    let fileToUpload = event.target.files;
    let file = fileToUpload[0] as File;
    let reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      let base64String = reader.result as string;
      parameter.value = JSON.stringify({data: base64String.replace(`data:${file.type};base64,`, ''), type: file.type, name: file.name});
      parameter.preselectedValue = file.name;
    };
  }

  confirm(): void {
    if (!this.signatureImg && this.actionHasSignature) {
      this.app.alertError(this.app.translations.ALERT_Error_NoSignatureSubmitted);
      return;
    }

    if (this.invalidValues) {
      this.app.alertError(this.app.translations.ALERT_Error_InvalidValues);
      return;
    }

    if (this.selectedItems.length > 1) {
      this.showConfirmation = true;
      return;
    }

    for (let k = 0; k < this.parameters.length; k++) {
      if (this.parameters[k].highlightRequired) {
        this.parameters[k].highlightRequired = false;
      }
    }

    let emptyParams = this.parameters.filter(param => (param.value == '' || param.value == null) && param.mandatory == 'Y' && param.dataType != 'DateTime');
    let emptyDateParams = this.parameters.filter(param => (param.dateValues.serverDate == '' || param.dateValues.serverDate == null) && param.mandatory == 'Y' && param.dataType == 'DateTime');
    if (emptyParams.length > 0 || emptyDateParams.length > 0) {
      emptyParams.forEach(param => { param.highlightRequired = true; })
      this.app.alertError(this.app.translations.ALERT_Error_RequiredFieldEmpty);
    } else {
      this.app.showLoader();
      this.executeSave();
    }
  }

  async executeSave(): Promise<void> {
    let saveResult = '';
    if (this.selectedItems.length <= 1) {
      let id = this.selectedItems.length == 1 ? this.selectedItems[0].id : '';
      for (let i = 0; i < this.parameters.length; i++) {
        if (this.parameters[i] && this.parameters[i].value != '' && this.parameters[i].type == 'combolookup' && this.parameters[i].selectedComboId == '-1') {
          let lookupValue = this.parameters[i].lookupValues.find(lv => lv.value == this.parameters[i].value);
          if (lookupValue) {
            this.parameters[i].selectedComboId = lookupValue.id;
          }
        }
      }

      await this.radController.createUpdateSingleRecord(this.nodeId, this.userId, this.action.id, id, this.parameters).then(result => {
        saveResult += result;
      });
    } else {
      for (let i = 0; i < this.selectedItems.length; i++) {
        if (this.isSelectedLine(this.selectedItems[i])) {
          this.app.loaderText = `${i + 1} ${this.app.translations.GBL_lbl_Of} ${this.selectedItems.length} ${this.app.translations.GBL_lbl_Saved}`;
          let tempParams = JSON.parse(JSON.stringify(this.parameters)) as IParameter[];//create deepclone of parameters
          let item = this.selectedItems[i] as ISelectedLine;
          for (let key in item.lineData) {
            let param = tempParams.find(p => p.caption == key.toString());
            if (param && param.dataType == 'DateTime' && param.type.includes('date') && param.dateValues.serverDate == '') {
              let dateValues = item.lineData[key].toString().split('/');
              if (this.settings.dateFormatSetting == 'dd/MM/yyyy')
                param.dateValues.serverDate = `${dateValues[1]}/${dateValues[0]}/${dateValues[2]}`;

              if (this.settings.dateFormatSetting == 'yyyy/MM/dd')
                param.dateValues.serverDate = `${dateValues[1]}/${dateValues[2]}/${dateValues[0]}`;

              this.showDateFields = true;
            } else if (param && param.value == '' && item.lineData[key]) {
              param.value = item.lineData[key].toString();

              if (param.type == 'combolookup' && param.selectedComboId == '-1') {
                let lookupValue = param.lookupValues.find(lv => lv.value == param.value);
                if (lookupValue) param.selectedComboId = lookupValue.id;
              }
            }
          }

          await this.radController.createUpdateSingleRecord(this.nodeId, this.userId, this.action.id, item.id, tempParams).then(result => {
            saveResult += result;
          });
        }
      }
    }

    if (saveResult.includes('ERROR') || saveResult.includes('ERORR')) {
      if (saveResult.toLowerCase().includes('image')) {
        this.app.alertError(this.app.translations.ALERT_Error_ImageCannotProcess);
        return;
      }
    }

    if (saveResult == '') {
      this.parameters = [];
      this.app.alertSuccess(this.app.translations.ALERT_Success_Success);
      sessionStorage.removeItem('qx_action_idcheck');
      this.communicationService.refreshAll();
      return;
    }

    this.communicationService.showFeedbackDialog(saveResult);
  }

  testSQL(parameter: IParameter): void {
    this.validTestedSQL = false;
    this.showDataPreview = false;
    this.app.showLoader();
    parameter.errorMessage = '';
    this.radController.executeSqlStatement(parameter.value, this.userId).then(result => {
      this.app.hideLoader();
      if (result.message.includes('ERROR')) {
        parameter.errorMessage = result.message;
        return;
      }

      this.validTestedSQL = true;
      this.tableColumns = JSON.parse(result.tableColumns);
      this.tableData = JSON.parse(result.tableData);
    });
  }

  isSelectedLine(item: ISelectedLine | IGanttTask): item is ISelectedLine {
    return 'lineData' in item;
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event): void {
    if (window.innerWidth <= 500) this.mobileView = true;
  }
}